'use client';

import * as Switch from '@radix-ui/react-switch';
import clsx from 'clsx';
import { fromJS, Map as ImmutableMap, Set } from 'immutable';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FiInfo } from 'react-icons/fi';
import { MdOutlineFileDownload } from 'react-icons/md';
import Balancer from 'react-wrap-balancer';
import vegaEmbed, { vega } from 'vega-embed';

import currencyFormatter from 'shared/lib/currencyFormatter';
import { isBlank, isNumber, isPresent } from 'shared/lib/utils';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from 'components/ui/alert-dialog';
import { Button } from 'components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';
import { useToast } from 'components/ui/use-toast';

import { BACKEND_API_URL, graphqlClient, requestGraphql } from '@/lib/backend';
import {
  DEFAULT_CHART_AUTOSIZE,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
  SHARE_THUMBNAIL_HEIGHT,
} from '@/lib/config';
import deleteStory from '@/lib/deleteStory';
import { trackClick } from '@/lib/event_tracker';
import { usePrevious } from '@/lib/react_utils_client';
import { StateContext } from '@/lib/StateProvider';
import updateAnnotation from '@/lib/updateAnnotation';
import updateYAxis from '@/lib/updateYAxis';

import { Y_FIELD_LABEL_LAYER_PREFIX } from '@/lib/data/chart';
import { i18n } from '@/lib/data/i18n';
import * as StoryModel from '@/lib/data/story';
import { makeSpec as makeStorySpec } from '@/lib/data/story';
import * as User from '@/lib/data/user';
import { canonicalizeLayerName } from '@/lib/data/vega';

import AnnotationPopup from '@/components/AnnotationPopup';
import EditableField from '@/components/EditableField';
import StoryViews from '@/components/StoryViews';
import UserPictureAndUsername from '@/components/UserPictureAndUsername';
import YAxisLabelPopup from '@/components/YAxisLabelPopup';

import AnnotationsRenderer from '@/lib/renderers/AnnotationsRenderer';
import ChartSelectionRenderer from '@/lib/renderers/ChartSelectionRenderer';
import * as LineGlowRenderer from '@/lib/renderers/LineGlowRenderer';
import YAxisLabelsRenderer from '@/lib/renderers/YAxisLabelsRenderer';

import UpdateStoryMutation from '@/lib/queries/UpdateStoryMutation';

vega.expressionFunction('currencyFormatter', currencyFormatter);

async function updateStory(
  session,
  pathname,
  dispatch,
  story,
  storyAttributes,
  refresh
) {
  const graphql = graphqlClient(session);

  const { data, error } = await requestGraphql(
    session,
    graphql,
    pathname,
    UpdateStoryMutation,
    'updateStoryById',
    {
      id: story.get('id'),
      ...storyAttributes,
    },
    'Story Update',
    {
      storyId: story.get('id'),
      storySlug: story.get('slug'),
    }
  );

  if (error) {
    dispatch({
      type: 'setFlashMessage',
      data: {
        type: 'error',
        message: `${error.errorType}: ${error.errorMessage}`,
      },
    });

    return;
  }

  refresh();

  return data;
}

function shouldReRenderVega(previousStory, story) {
  if (!previousStory) {
    return true;
  }

  // NOTE: will be different when adding/removing annotations.
  if (
    previousStory.get('correlations').size !== story.get('correlations').size
  ) {
    return true;
  }

  // NOTE: will be different when changing annotation dates.
  if (
    previousStory.getIn(['annotationSet', 'annotations']).size ===
      story.getIn(['annotationSet', 'annotations']).size &&
    !previousStory
      .getIn(['annotationSet', 'annotations'])
      .map((a) => a.get('date'))
      .equals(
        story.getIn(['annotationSet', 'annotations']).map((a) => a.get('date'))
      )
  ) {
    return true;
  }

  // NOTE: will be different when changing the annotation set style.
  if (
    !previousStory
      .getIn(['annotationSet', 'style'])
      .equals(story.getIn(['annotationSet', 'style']))
  ) {
    return true;
  }

  // NOTE: will be different when changing specific annotation styles.
  const annotationStyles = Set(['showPointer', 'showLink']);
  if (
    !previousStory
      .getIn(['annotationSet', 'annotations'])
      .flatMap((annotation) =>
        annotation.get('style').map((_v, k) => annotationStyles.has(k))
      )
      .equals(
        story
          .getIn(['annotationSet', 'annotations'])
          .flatMap((annotation) =>
            annotation.get('style').map((_v, k) => annotationStyles.has(k))
          )
      )
  ) {
    return true;
  }

  // NOTE: will be different when narrowing.
  if (
    !previousStory
      .getIn(['chart', 'narrowedDateRange'])
      .equals(story.getIn(['chart', 'narrowedDateRange']))
  ) {
    return true;
  }

  // NOTE: will be different when changing chart styles.
  if (
    !previousStory
      .getIn(['chart', 'properties'])
      .equals(story.getIn(['chart', 'properties']))
  ) {
    return true;
  }

  // NOTE: will be different when changing y-field variable axis from left to
  // right or vice-versa.
  if (
    !previousStory
      .getIn(['chart', 'dualAxisChartProperties'])
      .equals(story.getIn(['chart', 'dualAxisChartProperties']))
  ) {
    return true;
  }

  // NOTE: will be different when changing chart bar chart properties.
  if (
    !previousStory
      .getIn(['chart', 'barChartProperties'])
      .equals(story.getIn(['chart', 'barChartProperties']))
  ) {
    return true;
  }

  // NOTE: will be different when changing chart time series properties.
  if (
    !previousStory
      .getIn(['chart', 'timeSeriesProperties'])
      .equals(story.getIn(['chart', 'timeSeriesProperties']))
  ) {
    return true;
  }

  // NOTE: will be different when choosing a different target axis for an
  // existing annotation.
  if (
    previousStory.getIn(['annotationSet', 'annotations']).size ===
      story.getIn(['annotationSet', 'annotations']).size &&
    !previousStory
      .getIn(['annotationSet', 'annotations'])
      .map((a) => a.get('targetChartDatasetColumn'))
      .equals(
        story
          .getIn(['annotationSet', 'annotations'])
          .map((a) => a.get('targetChartDatasetColumn'))
      )
  ) {
    return true;
  }
  // NOTE: will be different when changing y-field stack orders.
  if (
    !previousStory
      .getIn(['chart', 'datasets'])
      .map((dataset) =>
        dataset
          .getIn(['metadata', 'yAxes'])
          .map((yAxis) => yAxis.get('fieldOrder'))
      )
      .equals(
        story
          .getIn(['chart', 'datasets'])
          .map((dataset) =>
            dataset
              .getIn(['metadata', 'yAxes'])
              .map((yAxis) => yAxis.get('fieldOrder'))
          )
      )
  ) {
    return true;
  }

  // NOTE: will be different when changing y-axis styles.
  if (
    !previousStory
      .getIn(['chart', 'datasets'])
      .map((dataset) =>
        dataset.getIn(['metadata', 'yAxes']).map((yAxis) => yAxis.get('style'))
      )
      .equals(
        story
          .getIn(['chart', 'datasets'])
          .map((dataset) =>
            dataset
              .getIn(['metadata', 'yAxes'])
              .map((yAxis) => yAxis.get('style'))
          )
      )
  ) {
    return true;
  }

  // NOTE: will be different when toggling or renaming a chart field.
  if (
    !previousStory
      .getIn(['chart', 'datasets'])
      .map((dataset) =>
        dataset
          .getIn(['metadata', 'yAxes'])
          .map((yAxis) => yAxis.get('overrides'))
      )
      .equals(
        story
          .getIn(['chart', 'datasets'])
          .map((dataset) =>
            dataset
              .getIn(['metadata', 'yAxes'])
              .map((yAxis) => yAxis.get('overrides'))
          )
      )
  ) {
    return true;
  }

  return false;
}

const Story = ({
  sessionData,
  storyData,
  //////////////////////////////////////////////////////////////////////////////
  inEditMode,
  hideControls,
  hideTitleAuthorAndViews,
  hideViews,
  hideDownloadButton,
  hideContent,
  hideAnnotations,
  hideAllAnnotations,
  doNotRenderFallbackImage,
  titleAsLink,
  //////////////////////////////////////////////////////////////////////////////
  annotationsRenderer = new AnnotationsRenderer(),
  yAxisLabelsRenderer = new YAxisLabelsRenderer(),
  chartSelectionRenderer = new ChartSelectionRenderer(),
  // Query params. /////////////////////////////////////////////////////////////
  annotatedChartScreenshot,
  storyDownload,
  storyShare,
  chartWidth,
  chartHeight,
  // NOTE: 'fit' causes annotations to be rendered at different positions.
  chartAutosize,
  axisFontSize,
  //////////////////////////////////////////////////////////////////////////////
  pageType,
  rightSideLabelFontSize,
  isYAxisScaleZeroBased,
  //////////////////////////////////////////////////////////////////////////////
  id,
  className,
  titleClassName,
  byAuthorAdditionalClassName,
  viewsAdditionalClassName,
  //////////////////////////////////////////////////////////////////////////////
  initialDateRangeSelection,
  setSelectionStartDate,
  setSelectionEndDate,
  //////////////////////////////////////////////////////////////////////////////
  createAnnotationPopoverStateRef,
  createAnnotationPopoverState,
  setCreateAnnotationPopoverState,
}) => {
  const { dispatch } = useContext(StateContext);
  const session = useMemo(() => fromJS(sessionData), [sessionData]);
  const story = useMemo(() => fromJS(storyData), [storyData]);
  // TODO: make it work with multiple datasets.
  const dataset = story.getIn(['chart', 'datasets', 0]);

  const currentUser = session.get('user');

  const router = useRouter();
  const pathname = usePathname();
  const { toast } = useToast();

  const chartRef = useRef(null);

  const storyContainerRef = useRef(null);
  const titleRef = useRef(null);
  const summaryRef = useRef(null);
  const footnotesRef = useRef(null);
  const [storyShareChartHeight, setStoryShareChartHeight] = useState(null);

  useLayoutEffect(() => {
    if (!storyShare) {
      return () => {
        // Empty.
      };
    }

    const storyContainerPaddingTop = parseInt(
      window.getComputedStyle(storyContainerRef.current).paddingTop
    );
    const titleHeight = titleRef.current && titleRef.current.clientHeight;
    const summaryHeight = summaryRef.current && summaryRef.current.clientHeight;
    const footnotesHeight =
      footnotesRef.current && footnotesRef.current.clientHeight;
    if (
      !(
        isNumber(titleHeight) &&
        isNumber(summaryHeight) &&
        isNumber(footnotesHeight)
      )
    ) {
      return () => {
        // Empty.
      };
    }

    setStoryShareChartHeight(
      SHARE_THUMBNAIL_HEIGHT -
        (storyContainerPaddingTop +
          titleHeight +
          summaryHeight +
          footnotesHeight)
    );

    return () => {
      // Empty.
    };
  }, [storyShare]);

  const graphql = graphqlClient(session);

  const [areAnnotationPopupsDisabled, setAreAnnotationPopupsDisabled] =
    useState(false);
  const [areYAxisLabelPopupsDisabled, setAreYAxisLabelPopupsDisabled] =
    useState(false);

  // NOTE: these are going to be the SVG elements created by the "annotations
  // renderer". They'll be used as target elements for the annotation popup.
  const [annotationGroupElsById, setAnnotationGroupElsById] = useState(
    new Map()
  );

  // NOTE: these are going to be the SVG elements created by Vega. They'll be
  // used as target elements for the y-axis label popup.
  const [
    yAxisLabelGroupElsByOriginalName,
    setYAxisLabelGroupElsByOriginalName,
  ] = useState(new Map());

  const vegaEmbedPromise = useRef(null);
  const [isVegaEmbedRendering, setIsVegaEmbedRendering] = useState(null);
  const [didVegaRenderAtLeastOnce, setDidVegaRenderAtLeastOnce] =
    useState(null);
  const previousStory = usePrevious(story);

  // Enable/disable popups whether or not the "insert annotation popover" is
  // shown.
  const previousCreateAnnotationPopoverState = usePrevious(
    createAnnotationPopoverState
  );
  useEffect(() => {
    if (createAnnotationPopoverState && !previousCreateAnnotationPopoverState) {
      setAreAnnotationPopupsDisabled(true);
      setAreYAxisLabelPopupsDisabled(true);
    }

    if (!createAnnotationPopoverState && previousCreateAnnotationPopoverState) {
      setAreAnnotationPopupsDisabled(false);
      setAreYAxisLabelPopupsDisabled(false);
    }
  }, [createAnnotationPopoverState]);

  function updateAnnotationByIdAndAttributes(
    annotationId,
    annotationAttributes
  ) {
    return updateAnnotation(
      session,
      graphql,
      pathname,
      dispatch,
      router,
      annotationsRenderer,
      story,
      annotationsById.get(annotationId),
      annotationAttributes
    );
  }

  const _shouldReRenderVega = useCallback(shouldReRenderVega, [story]);

  useLayoutEffect(() => {
    if (!_shouldReRenderVega(previousStory, story)) {
      return function cleanup() {
        // Empty cleanup.
      };
    }

    if (isVegaEmbedRendering === true) {
      return function cleanup() {
        // Empty cleanup.
      };
    }

    const storySpec = makeStorySpec(story, {
      chart: {
        axisFontSize,
        rightSideLabelFontSize,
        isYAxisScaleZeroBased,
      },
      annotationSet: {
        hideAllAnnotations,
        hideAnnotations: new Set(hideAnnotations),
      },
    });

    const spec = {
      autosize: chartAutosize || DEFAULT_CHART_AUTOSIZE,
      width:
        chartWidth ||
        story.getIn(['chart', 'properties', 'width']) ||
        DEFAULT_CHART_WIDTH,
      height:
        chartHeight ||
        story.getIn(['chart', 'properties', 'height']) ||
        DEFAULT_CHART_HEIGHT,
      background: 'transparent',
      ...storySpec,
    };

    // console.log(JSON.stringify(spec, null, '  '));

    setIsVegaEmbedRendering(true);

    // eslint-disable-next-line no-param-reassign
    vegaEmbedPromise.current = vegaEmbed(chartRef.current, spec, {
      renderer: 'svg',
      actions: false,
      hover: false,
      tooltip: false,
    });

    vegaEmbedPromise.current.then(({ _view }) => {
      setIsVegaEmbedRendering(false);
      setDidVegaRenderAtLeastOnce(true);
    });
    // NOTE: not `.catch(console.error)`ing so that the stack trace is available
    // in devtools.

    return function cleanup() {
      if (vegaEmbedPromise.current) {
        setIsVegaEmbedRendering(null);
        chartSelectionRenderer.destroy();
        vegaEmbedPromise.current.then((embedResult) => embedResult.finalize());
      }
    };
  }, [story]);

  // NOTE: this is an effect because renderers expect the SVG element to be
  // "stable" in the DOM, which is not the case when the chart fallback image is
  // being removed from the DOM conditional to `isVegaEmbedRendering`
  // concurrently to the SVG element being constructed.
  useLayoutEffect(() => {
    if (isVegaEmbedRendering !== false) {
      return function cleanup() {
        // Empty cleanup.
      };
    }

    function maybeInitializeAnnotationsRenderer({ _view }) {
      if (
        hideAllAnnotations ||
        story.getIn(['annotationSet', 'annotations']).isEmpty()
      ) {
        return;
      }

      annotationsRenderer.render({
        el: chartRef.current,
        story,
        onAnnotationDragStart: () => {
          setAreAnnotationPopupsDisabled(true);
          chartSelectionRenderer.hideHover();
        },
        onAnnotationDragEnd: (_annotationId, _positionAtDragEng) => {
          setAreAnnotationPopupsDisabled(false);
        },
        shouldSetUpDraggableAnnotations: inEditMode,
        setAnnotationGroupElsById,
        updateAnnotationByIdAndAttributes,
      });
    }

    function updateYAxisByAttributes(yAxis, yAxisAttributes) {
      return updateYAxis(
        session,
        graphql,
        pathname,
        dispatch,
        router,
        yAxisLabelsRenderer,
        story,
        dataset,
        yAxis.get('name'),
        yAxisAttributes
      );
    }

    function maybeInitializeYAxisLabelsRenderer(
      { _view },
      yAxisLabelGroupElsByOriginalName
    ) {
      yAxisLabelsRenderer.render({
        el: chartRef.current,
        yAxes: dataset.getIn(['metadata', 'yAxes']),
        labelGroupElsByOriginalName: yAxisLabelGroupElsByOriginalName,
        shouldSetUpDraggableLabels: inEditMode,
        onLabelDragStart: () => {
          chartSelectionRenderer.hideHover();

          setAreAnnotationPopupsDisabled(true);
          setAreYAxisLabelPopupsDisabled(true);
        },
        onLabelDragEnd: () => {
          setAreAnnotationPopupsDisabled(false);
          setAreYAxisLabelPopupsDisabled(false);
        },
        setAnnotationGroupElsById,
        updateYAxisByAttributes,
      });
    }

    function maybeInitializeChartSelectionRenderer({ view }) {
      if (chartSelectionRenderer.isInitialized()) {
        return;
      }

      if (inEditMode || initialDateRangeSelection) {
        const svgEl = view.container().querySelector('svg');
        const backgroundEl = svgEl.querySelector('.background');

        chartSelectionRenderer.initialize(
          svgEl,
          backgroundEl,
          view.scale('x'),
          setAreAnnotationPopupsDisabled,
          setSelectionStartDate,
          setSelectionEndDate,
          createAnnotationPopoverStateRef,
          setCreateAnnotationPopoverState,
          initialDateRangeSelection,
          story
        );

        if (initialDateRangeSelection) {
          chartSelectionRenderer.renderInitialDateRangeSelection(
            initialDateRangeSelection
          );
        }
      }
    }

    vegaEmbedPromise.current.then(({ view }) => {
      const svgEl = view.container().querySelector('svg');

      const yAxisLabelGroupElsByOriginalName = Array.from(
        svgEl
          .querySelectorAll(
            `g[class*="${canonicalizeLayerName(Y_FIELD_LABEL_LAYER_PREFIX)}"]`
          )
          .entries()
      ).reduce((memo, [_i, yAxisLabelGroupEl]) => {
        const yAxisLabelTextEl = yAxisLabelGroupEl.querySelector('text');

        if (yAxisLabelTextEl === null) {
          return memo;
        }

        const { yAxis } = yAxisLabelTextEl.__data__.datum.fieldLabel;
        return memo.set(yAxis.name, yAxisLabelGroupEl);
      }, new Map());

      setYAxisLabelGroupElsByOriginalName(yAxisLabelGroupElsByOriginalName);

      maybeInitializeYAxisLabelsRenderer(
        { view },
        yAxisLabelGroupElsByOriginalName
      );
    });
    vegaEmbedPromise.current.then(({ _view }) => {
      LineGlowRenderer.render(chartRef.current, story);
    });
    vegaEmbedPromise.current.then(maybeInitializeAnnotationsRenderer);
    vegaEmbedPromise.current.then(maybeInitializeChartSelectionRenderer);
  }, [isVegaEmbedRendering]);

  if (!story) {
    console.warn('No story object');
    return null;
  }

  const annotationSet = story.get('annotationSet');
  const annotations = annotationSet.get('annotations');
  const annotationsById = annotations.reduce((memo, annotation) => {
    return memo.set(annotation.get('id'), annotation);
  }, ImmutableMap());

  function makeOnBlurHandler(field) {
    return (_e, newValue) => {
      return updateStory(
        session,
        pathname,
        dispatch,
        story,
        { [field]: newValue },
        router.refresh
      );
    };
  }

  let storyTitle;

  if (inEditMode) {
    storyTitle = (
      <EditableField
        className={clsx([
          'placeholder:font-light placeholder:italic',
          titleClassName,
        ])}
        disableWhitespaceWrap={true}
        element="textarea"
        isEditing={inEditMode}
        fieldPlaceholder="Untitled Story"
        originalValue={story.get('title')}
        onBlur={makeOnBlurHandler('title')}
      />
    );
  } else {
    const h1Title = (
      <h1
        className={clsx(
          storyShare &&
            // NOTE: dimensions from `SHARE_THUMBNAIL_WIDTH` and
            // `SHARE_THUMBNAIL_HEIGHT`.
            'w-full items-center overflow-x-clip overflow-ellipsis whitespace-nowrap text-center !text-[30px] font-bold !leading-[1] tracking-tighter',
          titleClassName
        )}
      >
        {storyShare ? (
          story.get('title')
        ) : (
          <Balancer>{story.get('title')}</Balancer>
        )}
      </h1>
    );

    if (titleAsLink) {
      storyTitle = (
        <Link
          href={`/${story.getIn(['author', 'username'])}/${story.get('slug')}`}
        >
          {h1Title}
        </Link>
      );
    } else {
      storyTitle = h1Title;
    }
  }

  return (
    <div id={id} className={clsx(['flex flex-col'], className)}>
      {/* Story controls ("unlist/publish" + "edit/finish-editing"). */}
      {(() => {
        if (
          hideControls ||
          annotatedChartScreenshot ||
          storyDownload ||
          storyShare ||
          !User.isStoryAuthor(currentUser, story)
        ) {
          return null;
        }

        return (
          <div className={clsx('my-4 flex h-fit')}>
            {(() => {
              if (inEditMode) {
                return (
                  <Link
                    type="button"
                    className="mb-2 whitespace-nowrap rounded-lg bg-blue-500 px-5 py-2.5 text-lg font-medium text-white hover:bg-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-300 dark:bg-blue-500 dark:hover:bg-blue-600 dark:focus:ring-blue-800"
                    href={`/${story.getIn(['author', 'username'])}/${story.get(
                      'slug'
                    )}`}
                  >
                    <span>Finish editing story</span>
                  </Link>
                );
              }

              return (
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-12">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-2">
                      <label
                        className="hover:cursor-pointer"
                        htmlFor="unlisted-or-published-switch"
                      >
                        {StoryModel.isPublished(story)
                          ? 'Published'
                          : 'Unlisted'}
                      </label>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <FiInfo className="text-gray-500" />
                          </TooltipTrigger>

                          <TooltipContent>
                            <p className="max-w-[40ch] text-gray-700">
                              {(() => {
                                if (StoryModel.isPublished(story)) {
                                  return (
                                    <div className="flex flex-col gap-2">
                                      <p>
                                        Published means that this story will
                                        appear in the &quot;Stories&quot; tab.
                                      </p>

                                      <p>
                                        Click the toggle to make only people who
                                        know the story URL be able to see it.
                                      </p>
                                    </div>
                                  );
                                }

                                return (
                                  <div className="flex flex-col gap-2">
                                    <p>
                                      Unlisted means that only people who know
                                      the story URL can see it.
                                    </p>

                                    <p>
                                      Click the toggle to make it appear in the
                                      &quot;Stories&quot; tab.
                                    </p>
                                  </div>
                                );
                              })()}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </span>

                    <Switch.Root
                      id="unlisted-or-published-switch"
                      checked={StoryModel.isPublished(story)}
                      className="relative block h-8 w-14 rounded-full bg-gray-600 data-[state=checked]:bg-lime-500"
                      aria-label="Published?"
                      onCheckedChange={(_e) => {
                        updateStory(
                          session,
                          pathname,
                          dispatch,
                          story,
                          {
                            state:
                              StoryModel.toggleStateBetweenUnlistedAndPublished(
                                story
                              ),
                          },
                          router.refresh
                        );

                        trackClick(
                          session,
                          pathname,
                          'Toggle Published Button',
                          {
                            storyId: story.get('id'),
                            pageType,
                          }
                        );
                      }}
                    >
                      <Switch.Thumb className="dot absolute left-1 top-1 h-6 w-6 rounded-full bg-white transition data-[state=checked]:translate-x-6" />
                    </Switch.Root>
                  </div>

                  <Button
                    asChild
                    variant="secondary"
                    size=""
                    className="text-lg"
                  >
                    <Link
                      href={`/${story.getIn([
                        'author',
                        'username',
                      ])}/${story.get('slug')}?edit=true`}
                    >
                      Edit story
                    </Link>
                  </Button>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="link"
                        className="text-gray-500"
                        onClick={() => {
                          trackClick(session, pathname, 'Delete Story Button', {
                            storyId: story.get('id'),
                            pageType,
                          });
                        }}
                      >
                        Delete story
                      </Button>
                    </AlertDialogTrigger>

                    <AlertDialogContent className="">
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          Are you absolutely sure?
                        </AlertDialogTitle>

                        <AlertDialogDescription>
                          This action cannot be undone. This will permanently
                          delete your story and remove its data from our
                          servers.
                        </AlertDialogDescription>
                      </AlertDialogHeader>

                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>

                        <AlertDialogAction
                          onClick={async () => {
                            const { error } = await deleteStory(
                              session,
                              graphql,
                              pathname,
                              dispatch,
                              router,
                              story,
                              { shouldRefreshRouter: true }
                            );

                            if (error) {
                              return;
                            }

                            router.replace(`/${currentUser.get('username')}`);

                            toast({
                              title: 'Story deleted',
                              description: story.get('title'),
                            });
                          }}
                        >
                          Continue
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              );
            })()}
          </div>
        );
      })()}

      {/* Story container. */}
      <div
        // NOTE: this element ID is used in the web driver component.
        id={`story-${story.get('id')}`}
        ref={storyContainerRef}
        className={clsx([
          'flex flex-col',
          storyDownload && 'p-4',
          // NOTE: dimensions from `SHARE_THUMBNAIL_WIDTH` and
          // `SHARE_THUMBNAIL_HEIGHT`.
          //
          // NOTE: make sure the vertical padding is in pixels to not break
          // parsing!
          storyShare
            ? 'h-[418px] w-[800px] items-center px-8 pt-[8px]'
            : 'w-full max-w-screen-xl',
        ])}
      >
        {/* Story title, author and views. */}
        {(() => {
          if (annotatedChartScreenshot || hideTitleAuthorAndViews) {
            return null;
          }

          return (
            <div
              className={clsx(
                'w-full',
                storyShare ? '' : 'mb-2 flex w-full flex-col gap-1'
              )}
            >
              {/* Story title. */}
              <div ref={titleRef} className={'font-lora w-full'}>
                {storyTitle}
              </div>

              {/* By author. */}
              {(() => {
                if (storyDownload || storyShare) {
                  return null;
                }

                return (
                  <div
                    className={clsx(
                      'flex items-center space-x-1',
                      byAuthorAdditionalClassName
                    )}
                  >
                    <span className="text-gray-600">
                      {i18n(story.get('language'), 'by')}
                    </span>

                    <UserPictureAndUsername
                      user={story.get('author')}
                      width={32}
                      height={32}
                    />
                  </div>
                );
              })()}

              {/* Views + download button. */}
              {(() => {
                if (storyDownload || storyShare || inEditMode) {
                  return null;
                }

                let storyImageUrl = new URL(
                  `${BACKEND_API_URL}/download/${story.getIn([
                    'author',
                    'username',
                  ])}/${story.get('slug')}.png?v=${story.get(
                    'lastScreenshottedAt'
                  )}`
                );

                return (
                  <div className="flex items-center gap-4">
                    {(() => {
                      if (hideViews) {
                        return null;
                      }

                      return (
                        <StoryViews
                          additionalClassName={viewsAdditionalClassName}
                          story={story}
                        />
                      );
                    })()}

                    {(() => {
                      if (hideDownloadButton) {
                        return null;
                      }

                      return (
                        <Button
                          asChild
                          variant="link"
                          onClick={() => {
                            trackClick(
                              session,
                              pathname,
                              'Download Story PNG Button',
                              {
                                storyId: story.get('id'),
                                pageType,
                              }
                            );
                          }}
                        >
                          <a
                            href={storyImageUrl.toString()}
                            className="flex flex-row items-center gap-1 text-gray-500"
                            download
                          >
                            <MdOutlineFileDownload />

                            <span className="">Download PNG</span>
                          </a>
                        </Button>
                      );
                    })()}
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {/* Story before chart content. */}
        {(() => {
          if (
            hideContent ||
            annotatedChartScreenshot ||
            storyDownload ||
            storyShare ||
            (!inEditMode && isBlank(story.get('beforeChartContent')))
          ) {
            return null;
          }

          return (
            <EditableField
              markdown
              className="my-4 max-w-prose placeholder:font-light placeholder:italic"
              isEditing={inEditMode}
              element="textarea"
              fieldPlaceholder="(Optional) Add commentary about the story that will be above the chart"
              originalValue={story.get('beforeChartContent')}
              onBlur={makeOnBlurHandler('beforeChartContent')}
            />
          );
        })()}

        {/* Story summary. */}
        <div ref={summaryRef}>
          {(() => {
            if (hideContent || annotatedChartScreenshot) {
              return null;
            }

            return (
              <EditableField
                className={clsx(
                  storyDownload && 'text-lg',
                  storyShare
                    ? isPresent(story.get('summary')) &&
                        // NOTE: using vertical padding because vertical margin
                        // isn't computed in `clientHeight`. Also note that the
                        // vertical padding is related to the line height.
                        'line-clamp-2 w-[inherit] px-8 py-[4px] text-center text-[16px] text-sm leading-[20px] text-gray-700 [text-wrap:balance]'
                    : 'mb-4 max-w-prose placeholder:font-light placeholder:italic'
                )}
                isEditing={inEditMode}
                element="textarea"
                fieldPlaceholder="(Optional) Add a short summary that will appear together with and right above the chart"
                originalValue={story.get('summary')}
                onBlur={makeOnBlurHandler('summary')}
              />
            );
          })()}
        </div>

        {/* Story annotated chart screenshot or story SVG. */}
        {/* For whatever reason, the "flex" in the parent div of the "either
            image or Vega SVG" fixes the situation where both are rendered at
            the same time for a split second, which causes a layout shift.  */}
        <div className="flex">
          {(() => {
            if (
              inEditMode ||
              storyDownload ||
              storyShare ||
              annotatedChartScreenshot ||
              doNotRenderFallbackImage
            ) {
              return null;
            }

            if (didVegaRenderAtLeastOnce === true) {
              return null;
            }

            if (isVegaEmbedRendering === false) {
              return null;
            }

            let storyImageUrl = new URL(
              `${BACKEND_API_URL}/stories/${story.getIn([
                'author',
                'username',
              ])}/${story.get('slug')}.png?v=${story.get(
                'lastScreenshottedAt'
              )}`
            );

            if (axisFontSize) {
              storyImageUrl.searchParams.set('axis_font_size', axisFontSize);
            }

            if (rightSideLabelFontSize) {
              storyImageUrl.searchParams.set(
                'right_side_label_font_size',
                rightSideLabelFontSize
              );
            }

            // NOTE: width and height 100% to match the size of the Vega chart
            // and minimize layout shifts. Check `div[role='graphics-document']
            // svg` in globals.css.
            return (
              <Image
                className="h-full w-full"
                src={storyImageUrl.toString()}
                alt={story.get('title')}
                width={DEFAULT_CHART_WIDTH}
                height={DEFAULT_CHART_HEIGHT}
              />
            );
          })()}

          <div
            id={`story-${story.get('id')}-svg-wrapper`}
            ref={chartRef}
            className={clsx(
              'w-full select-none',
              inEditMode && 'border border-gray-200 dark:border-gray-800'
            )}
            style={
              storyShareChartHeight
                ? { height: `${storyShareChartHeight}px` }
                : {}
            }
          ></div>
        </div>

        {/* Story footnotes. */}
        <div ref={footnotesRef} className="flex w-full">
          {(() => {
            if (
              (hideContent || annotatedChartScreenshot) &&
              !(storyDownload || storyShare)
            ) {
              return null;
            }

            return (
              <div
                className={clsx(
                  'flex items-center',
                  storyShare
                    ? isPresent(story.get('footnotes')) &&
                        // NOTE: using vertical padding because vertical margin
                        // isn't computed in `clientHeight`. Also note that the
                        // vertical padding is related to the line height.
                        'left-0 mb-0 w-[inherit]'
                    : 'mt-2 w-full'
                )}
              >
                <EditableField
                  className={clsx(
                    (storyDownload || storyShare) && 'italic text-gray-600',
                    storyDownload && 'text-lg',
                    storyShare
                      ? isPresent(story.get('footnotes')) &&
                          // NOTE: using vertical padding because vertical margin
                          // isn't computed in `clientHeight`. Also note that the
                          // vertical padding is related to the line height.
                          'line-clamp-2 pr-8 text-[14px] text-sm leading-[20px]'
                      : 'max-w-prose placeholder:font-light placeholder:italic'
                  )}
                  isEditing={inEditMode}
                  element="textarea"
                  fieldPlaceholder="(Optional) Add a short footnote that will appear together with and right below the chart"
                  originalValue={story.get('footnotes')}
                  onBlur={makeOnBlurHandler('footnotes')}
                />

                {/* contextualize.ai logo. */}
                {(() => {
                  if (!(storyDownload || storyShare)) {
                    return null;
                  }

                  return (
                    <div className="right-0 ml-auto flex items-center">
                      <Image
                        className={clsx(
                          storyShare && 'min-h-[30px] min-w-[30px]',
                          storyDownload && 'min-h-[40px] min-w-[40px]'
                        )}
                        alt="contextualize.ai logo"
                        width={storyShare ? 30 : storyDownload ? 40 : 30}
                        height={storyShare ? 30 : storyDownload ? 40 : 30}
                        src="/contextualize.ai logo 50x50.png"
                      />

                      <span
                        className={clsx(
                          'font-signika-negative whitespace-nowrap font-semibold text-gray-500 dark:text-white',
                          storyDownload && 'text-xl'
                        )}
                      >
                        contextualize.ai
                      </span>
                    </div>
                  );
                })()}
              </div>
            );
          })()}
        </div>

        {/* Story after chart content. */}
        {(() => {
          if (
            hideContent ||
            annotatedChartScreenshot ||
            storyDownload ||
            storyShare ||
            (!inEditMode && isBlank(story.get('afterChartContent')))
          ) {
            return null;
          }

          return (
            <div className="mt-8 flex w-full flex-col gap-1">
              <EditableField
                markdown
                className="max-w-prose placeholder:font-light placeholder:italic"
                isEditing={inEditMode}
                element="textarea"
                fieldPlaceholder="(Optional) Add commentary about the story that will be below the chart"
                originalValue={story.get('afterChartContent')}
                onBlur={makeOnBlurHandler('afterChartContent')}
              />
            </div>
          );
        })()}
      </div>

      {!annotatedChartScreenshot &&
        story.getIn(['annotationSet', 'annotations']).map((annotation) => {
          const annotationId = annotation.get('id');
          const annotationGroupEl = annotationGroupElsById.get(annotationId);

          if (!annotationGroupEl) {
            return null;
          }

          const annotationHoverableEl =
            annotationGroupEl.querySelector('rect.foreground');

          if (!annotationHoverableEl) {
            return null;
          }

          return (
            <div key={annotationId}>
              <AnnotationPopup
                session={session}
                annotationId={annotationId}
                annotationsRenderer={annotationsRenderer}
                annotationHoverableEl={annotationHoverableEl}
                annotation={annotation}
                inEditMode={inEditMode}
                story={story}
                isDisabled={areAnnotationPopupsDisabled}
                onShow={() => {
                  if (!inEditMode) {
                    return;
                  }

                  chartSelectionRenderer.hideHover();
                  setAreYAxisLabelPopupsDisabled(true);
                }}
                onHide={() => {
                  if (!inEditMode) {
                    return;
                  }

                  setAreYAxisLabelPopupsDisabled(false);
                }}
              />
            </div>
          );
        })}

      {!annotatedChartScreenshot &&
        inEditMode &&
        !areYAxisLabelPopupsDisabled &&
        yAxisLabelGroupElsByOriginalName.size > 0 &&
        dataset.getIn(['metadata', 'yAxes']).map((yAxis) => {
          const yAxisLabelGroupEl = yAxisLabelGroupElsByOriginalName.get(
            yAxis.get('name')
          );

          if (!yAxisLabelGroupEl) {
            return null;
          }

          return (
            <div key={yAxis.get('name')}>
              <YAxisLabelPopup
                session={session}
                yAxisOriginalName={yAxis.get('name')}
                hoverableEl={yAxisLabelGroupEl}
                yAxisLabelsRenderer={yAxisLabelsRenderer}
                yAxis={yAxis}
                inEditMode={inEditMode}
                story={story}
                dataset={dataset}
                isDisabled={areYAxisLabelPopupsDisabled}
                onShow={() => {
                  setAreAnnotationPopupsDisabled(true);
                  chartSelectionRenderer.hideHover();
                }}
                onHide={() => {
                  setAreAnnotationPopupsDisabled(false);
                }}
              />
            </div>
          );
        })}
    </div>
  );
};

export default Story;
